/* eslint-disable react/no-unstable-nested-components */
import React, {useEffect, useRef} from 'react';

import {createBottomTabNavigator} from '@react-navigation/bottom-tabs';
import {RFValue} from 'react-native-responsive-fontsize';
import Ionicons from 'react-native-vector-icons/dist/Ionicons';

import { useTheme } from 'styled-components/native';
import {Animated, StyleSheet, View} from 'react-native';
import analytics from '@react-native-firebase/analytics';

function PulsingDot({color}: {color: string}) {
  const ring1 = useRef(new Animated.Value(0)).current;
  const ring2 = useRef(new Animated.Value(0)).current;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    ring1.setValue(0);
    ring2.setValue(0);

    const DURATION = 1800;
    const PAUSE = 600;

    const makeLoop = (anim: Animated.Value) =>
      Animated.loop(
        Animated.sequence([
          Animated.timing(anim, {toValue: 1, duration: DURATION, useNativeDriver: true}),
          Animated.timing(anim, {toValue: 0, duration: 0, useNativeDriver: true}),
          // Animated.delay usa useNativeDriver: false internamente (fixo no
          // react-native), o que cria um interaction handle a cada ciclo do
          // loop e bloqueia o InteractionManager pra sempre enquanto o alerta
          // estiver ativo. Um timing "parado" em 0 com useNativeDriver: true
          // produz a mesma pausa visual sem esse efeito colateral.
          Animated.timing(anim, {toValue: 0, duration: PAUSE, useNativeDriver: true}),
        ]),
      );

    const loop1 = makeLoop(ring1);
    const loop2 = makeLoop(ring2);

    loop1.start();
    timerRef.current = setTimeout(() => loop2.start(), 900);

    return () => {
      loop1.stop();
      loop2.stop();
      if (timerRef.current) {clearTimeout(timerRef.current);}
      ring1.setValue(0);
      ring2.setValue(0);
    };
  }, [ring1, ring2]);

  const ringStyle = (anim: Animated.Value) => ({
    opacity: anim.interpolate({inputRange: [0, 0.06, 0.5, 1], outputRange: [0, 0.9, 0.5, 0]}),
    transform: [{scale: anim.interpolate({inputRange: [0, 1], outputRange: [1, 2.0]})}],
  });

  return (
    <Animated.View
      style={{
        position: 'absolute',
        top: -2,
        right: -4,
        width: 8,
        height: 8,
        borderRadius: 4,
      }}>
      <Animated.View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFillObject,
          {borderRadius: 4, backgroundColor: color},
          ringStyle(ring1),
        ]}
      />
      <Animated.View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFillObject,
          {borderRadius: 4, backgroundColor: color},
          ringStyle(ring2),
        ]}
      />
      <Animated.View
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: color,
        }}
      />
    </Animated.View>
  );
}

export const {Navigator, Screen} = createBottomTabNavigator();

import {Home} from '../screens/Home';
import {Shopping} from '../screens/Shopping';
import {Emprestimo} from '../screens/Emprestimo';
import {FloatingTabBar} from './components/FloatingTabBar';

import {Coupons} from '../screens/Coupons';
import {PP_SearchDebits} from '../screens/ProntoPaguei/VehicleDebits/SearchDebits';
import {Step} from '../components/Interface/Step';
import {useNavigation} from '@react-navigation/native';
import {useAuth} from '../context/auth';
import {EmprestimoAlertsProvider, useEmprestimoAlerts} from '../context/EmprestimoAlerts';

import Lottie from 'lottie-react-native';

import {ShimmerIcon} from './ShimmerIcon';


const SHOPPING_GOLD_COLOR = '#FFD700';
const SHOPPING_ACCENT_COLOR = '#FFA500';

const ALERT_DOT_COLORS = {
  vencidas: '#EF4444',
  a_vencer: '#F97316',
  assinatura: '#3B82F6',
};

function AppTabNavInner() {
  const theme = useTheme();
  const navigation = useNavigation();
  const {isLogged, setShowSign, openSign} = useAuth();
  const {alertType} = useEmprestimoAlerts();

  return (
    <>
      <Navigator
        tabBar={props => <FloatingTabBar {...props} />}
        screenOptions={() => ({
          headerShown: false,
          tabBarStyle: {position: 'absolute'},
          tabBarActiveTintColor: theme.colors.title,
          tabBarInactiveTintColor: theme.colors.title,
          tabBarLabelPosition: 'below-icon',
        })}
        initialRouteName={'Initial'}>
        <Screen
          name="Initial"
          component={Home}
          options={{
            tabBarLabel: 'Início',
            tabBarIcon: ({size, color, focused}) => (
              //@ts-ignore
              <Ionicons
                name={focused ? 'home' : 'home-outline'}
                size={size}
                color={color}
              />
            ),
          }}
        />
        <Screen
          name="Coupons"
          component={Coupons}
          listeners={{
            tabPress: e => {
              if (!isLogged) {
                e.preventDefault();
                setShowSign(true);
              }
            },
          }}
          options={{
            tabBarLabel: 'Cupons',
            tabBarIcon: ({size, color, focused}) => (
              //@ts-ignore
              <Ionicons
                name={focused ? 'qr-code' : 'qr-code-outline'}
                size={size}
                color={color}
              />
            ),
          }}
        />
    <Screen
  name="Shopping"
  component={Shopping}
  options={{
    tabBarLabel: 'Comprar',
    unmountOnBlur: false,
    tabBarLabelStyle: {
      fontFamily: theme.fonts.semibold,
      fontSize: RFValue(9),
    },
    tabBarActiveTintColor: SHOPPING_ACCENT_COLOR,
    tabBarIcon: ({size, focused}) => (
      <Step
        order={3}
        contextName="welcome"
        text="Clique aqui para comprar!"
        onPress={() => navigation.navigate('Shopping')}
        screen="Initial">
        <ShimmerIcon
          size={size + RFValue(18)}
          colors={
            focused
              ? [SHOPPING_GOLD_COLOR, SHOPPING_ACCENT_COLOR, SHOPPING_GOLD_COLOR]
              : [SHOPPING_GOLD_COLOR, SHOPPING_ACCENT_COLOR, SHOPPING_GOLD_COLOR]
          }>
          {
            //@ts-ignore
            <Lottie
              source={require('../assets/images/json/cart.json')}
              autoPlay
              loop
              resizeMode="cover"
              style={{
                width: '55%',
                height: '55%',
              }}
            />
          }
        </ShimmerIcon>
      </Step>
    ),
  }}
  listeners={{
    tabPress: () => {
      analytics().logEvent('clica_nav_comprar');
    },
    }}
      />
        <Screen
          name="Emprestimo"
          component={Emprestimo}
           listeners={{
            tabPress: e => {
              if (!isLogged) {
                e.preventDefault();
                setShowSign(true);
              }
            },
          }}
          options={{
            tabBarLabel: 'Empréstimo',
            tabBarActiveTintColor: theme.colors.title,

            tabBarIcon: ({size, color, focused}) => (
              <View>
                {/*@ts-ignore*/}
                <Ionicons
                  name={focused ? 'cash-outline' : 'cash-outline'}
                  size={size}
                  color={color}
                />
                {alertType && (
                  <PulsingDot color={ALERT_DOT_COLORS[alertType]} />
                )}
              </View>
            ),
          }}
        />

        <Screen
          name="VehicleDebits"
          component={PP_SearchDebits}
          listeners={{
            tabPress: e => {
              if (!isLogged) {
                e.preventDefault();
                openSign();
              }
            },
          }}
          options={{
            tabBarLabel: 'Multas',
            tabBarIcon: ({size, color, focused}) => (
              //@ts-ignore
              <Ionicons
                name={focused ? 'car' : 'car-outline'}
                size={size}
                color={color}
              />
            ),
          }}
        />
      </Navigator>
    </>
  );
}

export function AppTabNav() {
  return (
    <EmprestimoAlertsProvider>
      <AppTabNavInner />
    </EmprestimoAlertsProvider>
  );
}
